import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { TranscriptSegment } from '@grove/api-client';
import { activeSegmentIndex, formatDuration } from '../lib/media';

interface Props {
  segments: TranscriptSegment[] | null | undefined;
  /** Current playback time in seconds — drives the highlight. */
  currentTime: number;
  /** Tap a segment to jump the player there. */
  onSeek: (seconds: number) => void;
}

/** How long after the user stops scrolling before auto-follow resumes. */
const FOLLOW_RESUME_MS = 3000;

/** Left gutter reserved for line-leading timestamps (fits "1:02:05"). */
const GUTTER_PX = 56;

/** A visual line's leading timestamp: its pixel offset + the seconds to show. */
interface LineMarker {
  top: number;
  seconds: number;
}

/**
 * Given each chunk's vertical pixel offset (in DOM order), return one marker
 * per visual line — the first chunk that lands on each distinct offset. Pure
 * so the wrap→timestamp mapping is easy to reason about.
 */
function lineMarkers(tops: number[], segments: TranscriptSegment[]): LineMarker[] {
  const markers: LineMarker[] = [];
  let prevTop = Number.NaN;
  for (let i = 0; i < tops.length; i++) {
    // offsetTop is integer px; a strict change means a new wrapped line.
    if (tops[i] !== prevTop) {
      markers.push({ top: tops[i], seconds: segments[i].start });
      prevTop = tops[i];
    }
  }
  return markers;
}

/**
 * Time-synced transcript rendered as flowing prose: chunks wrap inline like a
 * paragraph, the playing chunk is highlighted, and each wrapped line shows the
 * timestamp of its first chunk in a left gutter. Every chunk is tappable to
 * seek. Timestamps live in an absolutely-positioned gutter so adding them never
 * reflows the text (which would otherwise invalidate the line measurement).
 */
export function TranscriptPanel({ segments, currentTime, onSeek }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const paraRef = useRef<HTMLDivElement>(null);
  const userScrollingRef = useRef(false);
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [markers, setMarkers] = useState<LineMarker[]>([]);
  const activeIdx = segments ? activeSegmentIndex(segments, currentTime) : -1;

  const suspendFollow = () => {
    userScrollingRef.current = true;
    clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      userScrollingRef.current = false;
    }, FOLLOW_RESUME_MS);
  };

  useEffect(() => () => clearTimeout(resumeTimerRef.current), []);

  // Measure where chunks wrap and place one timestamp per visual line.
  // Re-runs when the segments change or the paragraph's width changes (window
  // resize, split-pane drag). Gutter markers are position:absolute, so writing
  // them back never shifts the text — the measurement is stable, no reflow loop.
  useLayoutEffect(() => {
    const para = paraRef.current;
    if (!para || !segments?.length) {
      setMarkers([]);
      return;
    }
    const measure = () => {
      const tops: number[] = [];
      for (let i = 0; i < segments.length; i++) {
        const el = para.querySelector<HTMLElement>(`[data-seg="${i}"]`);
        tops.push(el ? el.offsetTop : 0);
      }
      const next = lineMarkers(tops, segments);
      setMarkers((prev) =>
        prev.length === next.length && prev.every((m, i) => m.top === next[i].top && m.seconds === next[i].seconds)
          ? prev
          : next,
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(para);
    // Web fonts can load after first paint and shift wrapping.
    document.fonts?.ready.then(measure).catch(() => {});
    return () => ro.disconnect();
  }, [segments]);

  // Keep the active chunk in view, unless the user is scrolling by hand.
  useEffect(() => {
    if (activeIdx < 0 || userScrollingRef.current) return;
    const el = paraRef.current?.querySelector<HTMLElement>(`[data-seg="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [activeIdx]);

  if (!segments?.length) {
    return (
      <div className="rounded-xl p-5 mt-4 text-center" style={{ background: '#0E0E18', border: '1px solid #1E1E34' }}>
        <p className="text-sm" style={{ color: '#5C5C72' }}>Transcript unavailable for this video.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl mt-4 overflow-hidden" style={{ background: '#0E0E18', border: '1px solid #1E1E34' }}>
      <div className="px-4 py-2.5 border-b" style={{ borderColor: '#1E1E34' }}>
        <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#5C5C72' }}>Transcript</p>
      </div>
      <div
        ref={scrollRef}
        onWheel={suspendFollow}
        onTouchMove={suspendFollow}
        className="overflow-y-auto px-4 py-3"
        style={{ maxHeight: '50vh' }}
      >
        <div ref={paraRef} style={{ position: 'relative', paddingLeft: GUTTER_PX, lineHeight: 2 }}>
          {/* Line-leading timestamps, parked in the gutter (no text reflow). */}
          {markers.map((m) => (
            <span
              key={m.top}
              aria-hidden
              className="font-mono"
              style={{
                position: 'absolute',
                left: 0,
                top: m.top,
                width: GUTTER_PX - 10,
                textAlign: 'right',
                fontSize: '11px',
                lineHeight: 2,
                color: '#5C5C72',
                userSelect: 'none',
              }}
            >
              {formatDuration(m.seconds) ?? '0:00'}
            </span>
          ))}
          {/* Prose flow: one inline, tappable, individually-highlighted chunk each. */}
          {segments.map((seg, i) => {
            const active = i === activeIdx;
            return (
              <span
                key={i}
                data-seg={i}
                role="button"
                tabIndex={0}
                onClick={() => onSeek(seg.start)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSeek(seg.start);
                  }
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: '4px',
                  padding: '1px 2px',
                  fontSize: '15px',
                  transition: 'background-color 120ms, color 120ms',
                  background: active ? 'rgba(139,92,246,0.22)' : 'transparent',
                  color: active ? '#F0F0F8' : '#9898AC',
                }}
              >
                {seg.text}{' '}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
